import { useEffect } from 'react';
import { useMemo } from 'react';
import { useHistory, useParams } from 'react-router';
import { useTypedSelector } from '../../store/reducers/use-typed-selector';
import { BrandliveEvent, EventNavbar, HomepageFooter, HomepageHeader, ISocketCommentProps, PageModule, Session, BlProfile } from '../../types/working-model';

export interface stylingOverrideReturns {
	backgroundColor: string;
	backgroundImg: { backgroundImage: string } | { backgroundImage?: undefined };
	titleOverrides: string;
	descriptionOverrides: string;
	customHeadingOverrides: string;
	moduleTitleOverrides: string;
	moduleDescriptionOverrides: string;
	moduleCustomHeadingOverrides: string;
	primaryButtonOverrides: string;
	secondaryButtonOverrides: string;
	dropdownOverrides: string;
	startTimeOverrides: string;
}
export function getStylingOverrides(
	styling_overrides: PageModule['styling_overrides']
): stylingOverrideReturns {
	return useMemo(() => {
		const backgroundOverrides = styling_overrides?.background;
		const contentOverrides = styling_overrides?.content;
		const backgroundColor = styling_overrides?.background?.color ?? '';
		const backgroundImg = backgroundOverrides?.image.is_on
			? { backgroundImage: `url(${backgroundOverrides.image.src})` }
			: {};
		const titleOverrides = Object.values(contentOverrides?.title ?? {}).join(
			' '
		);
		const descriptionOverrides = Object.values(
			contentOverrides?.description ?? {}
		).join(' ');
		const customHeadingOverrides = Object.values(
			contentOverrides?.custom_heading ?? {}
		).join(' ');
		const moduleTitleOverrides = Object.values(
			contentOverrides?.modules?.title ?? {}
		).join(' ');
		const moduleDescriptionOverrides = Object.values(
			contentOverrides?.modules?.description ?? {}
		).join(' ');
		const moduleCustomHeadingOverrides = Object.values(
			contentOverrides?.modules?.custom_heading ?? {}
		).join(' ');
		const primaryButtonOverrides = Object.values(
			contentOverrides?.modules?.primary_button ?? {}
		).join(' ');
		const secondaryButtonOverrides = Object.values(
			contentOverrides?.modules?.secondary_button ?? {}
		).join(' ');
		const dropdownOverrides = Object.values(
			contentOverrides?.modules?.dropdowns ?? {}
		).join(' ');
		const startTimeOverrides = Object.values(
			contentOverrides?.start_time?.color ?? {}
		).join('');

		//Aside from backgroundImg these will all be inserted into the classnames of the element they reference

		// <div
		//      className={classNames("speakers", `speakers-${layout_type}`, template, backgroundColor)}
		//      style={backgroundImg}
		// >
		//      <div className="module-container">
		//          <p className={classNames("speakers-custom_heading", customHeadingOverrides)}>{content.custom_heading[language]}</p>
		//          <h2 className={classNames("speakers-title", titleOverrides)}>{content.title[language]}</h2>
		//          <p className={classNames("speakers-description", descriptionOverrides)}>{content.description[language]}</p>
		return {
			backgroundColor, //main module background color
			backgroundImg, //main module background image (goes under style attr -> style={backgroundImg})
			titleOverrides, // main module title
			descriptionOverrides, //main module description
			customHeadingOverrides, //main module aside or secondary title
			moduleTitleOverrides, //list item or card, main heading (e.g. SpeakerCard name element)
			moduleDescriptionOverrides, //card description
			moduleCustomHeadingOverrides, //card aside/price/time
			primaryButtonOverrides, //main button
			secondaryButtonOverrides, //alternate button
			dropdownOverrides, //dropdown and misc ui
			startTimeOverrides, //color of text for the start time
		};
	}, [styling_overrides]);
}

interface IHeaderStylingOverridesReturn {
	backgroundColor: string;
	backgroundImg: { backgroundImage: string } | { backgroundImage?: undefined };
	mainTextOverrides?: string;
	mainButtonOverrides: string;
}

export function getHeaderStylingOverrides(
	styling_overrides: HomepageHeader['styling_overrides']
): IHeaderStylingOverridesReturn {
	return useMemo(() => {
		const backgroundOverrides = styling_overrides?.background;
		const contentOverrides = styling_overrides?.content;
		const backgroundColor = styling_overrides?.background?.color ?? '';
		const backgroundImg = backgroundOverrides?.image.is_on
			? { backgroundImage: `url(${backgroundOverrides.image.src})` }
			: {};
		const mainTextOverrides = Object.values(contentOverrides?.navItems ?? {}).join(
			' '
		);
		const mainButtonOverrides = Object.values(
			contentOverrides?.callToAction ?? {}
		).join(' ');

		return {
			backgroundColor, //main module background color
			backgroundImg, //main module background image (goes under style attr -> style={backgroundImg})
			mainTextOverrides, // navItem overrides
			mainButtonOverrides // call to action overrides
		};
	}, [styling_overrides]);
}


interface IFooterStylingOverridesReturn {
	backgroundColor: string;
	backgroundImg: { backgroundImage: string; } | { backgroundImage?: undefined; };
	mainTextOverrides?: string;
	blLogoColor: string;
}

export function getFooterStylingOverrides(styling_overrides: HomepageFooter['styling_overrides']): IFooterStylingOverridesReturn {
	return useMemo(() => {
		const backgroundOverrides = styling_overrides?.background;
		const backgroundColor = backgroundOverrides?.color ?? '';
		const backgroundImg = backgroundOverrides?.image.is_on
			? { backgroundImage: `url(${backgroundOverrides.image.src})` }
			: {};

		const contentOverrides = styling_overrides?.content;
		const mainTextOverrides = Object.values(contentOverrides?.navItems ?? {}).join(' ');
		const blLogoColor = contentOverrides?.blLogoColor ?? '';

		return {
			backgroundColor, //main module background color
			backgroundImg, //main module background image (goes under style attr -> style={backgroundImg})
			mainTextOverrides, // navItem overrides
			blLogoColor // logo color overrides
		};
	}, [styling_overrides]);
}


export function getSessionStylingOverrides(styling_overrides: Session['layout']['styling_overrides']): Omit<IHeaderStylingOverridesReturn, 'mainButtonOverrides' | 'mainTextOverrides'> {

	const backgroundColor = styling_overrides?.background?.color ?? '';
	const backgroundImg = styling_overrides?.background?.image?.is_on
		? { backgroundImage: `url(${styling_overrides?.background?.image.src})`, backgroundSize: 'cover' }
		: {};

	return {
		backgroundColor, //main module background color
		backgroundImg, //main module background image (goes under style attr -> style={backgroundImg})
	};
}


export function eventPath(eventName?: string, uuid?: string): string {
	if (eventName) return eventName;
	else return `event/${uuid}`;
}

// FUTURE DEV: useParams is not correctly pulling in the eventName on brandlive-dev.com
// including this function for now to generate the correct url when forwarding to registration
// should remove once we figure out why params are not working
export function getEventNameURL(eventBundle: BrandliveEvent): string {
	return eventBundle?.name.toLowerCase().replace(/[^0-9a-z]/g, '-') ?? '';
}

export const blankStyleOverrides: stylingOverrideReturns = {
	backgroundColor: '',
	backgroundImg: { backgroundImage: '' },
	titleOverrides: '',
	descriptionOverrides: '',
	customHeadingOverrides: '',
	moduleTitleOverrides: '',
	moduleDescriptionOverrides: '',
	moduleCustomHeadingOverrides: '',
	primaryButtonOverrides: '',
	secondaryButtonOverrides: '',
	dropdownOverrides: '',
	startTimeOverrides: ''
};

//
export const useRedirectToHome = (shouldRedirect: boolean): void => {
	const eventBundle = useTypedSelector(state => state.LiveEventReducer?.eventBundle);

	const history = useHistory();
	const params = useParams() as any;
	const hasHomepage = !!eventBundle?.homepage;

	useEffect(() => {
		if (shouldRedirect) {
			const eventName = params?.eventName;
			const eventUuid = params?.uuid;
			const language = params?.language || 'en';
			const basePath = eventPath(eventName, eventUuid);
			const url = `/${basePath}/${language}${hasHomepage ? '/home' : ''}`;
			history.replace(url);
		}
	}, [
		shouldRedirect,
		eventBundle,
		params,
		hasHomepage,
		history,
	]);
};

interface IEventNavbarStylingReturn {
	backgroundColor: string;
	backgroundImg: { backgroundImage: string; } | { backgroundImage?: undefined; };
	navItemColor: string;
	navItemActiveAccent: string;
}

export const getEventNavbarStyling = (styling: EventNavbar['styling_overrides']): IEventNavbarStylingReturn => {
	const backgroundColor: string = styling?.background?.color ?? '';
	const backgroundImg: any = styling?.background?.image.is_on ? { backgroundImage: `url(${styling.background?.image.src})`, backgroundSize: 'cover' } : {};
	const navItemColor: string = styling?.content.navItems ?? '';
	const navItemActiveAccent: string = `${navItemColor.split('-')[0]}-background` ?? '';

	return { backgroundColor, backgroundImg, navItemColor, navItemActiveAccent };
};

export const getAvatarForComment = (channel: number, displayAvatar: boolean, comment: ISocketCommentProps): string => {
	return channel && displayAvatar && (comment?.registered_profile?.[channel]?.avatar || comment?.non_registered_profile?.[channel]?.avatar || comment?.profile?.[channel]?.avatar) || '';
};

// returns true if the registered or unregistered profile from the chat message matches the user blProfile
export function matchChatSender(blProfileUser: BlProfile | null, blProfile: number | undefined, blProfileNonReg: number | undefined): boolean {
	// profiles should be numbers but some chat functions are returning them as strings - using Number as safeguard
	return Number(blProfileUser?.bl_profile) === Number(blProfileUser?.non_registered ? blProfileNonReg : blProfile);
}